import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  LoadingState,
  Page,
} from "@marekl/adminer/components";
import { dayjs, toApiDate } from "@marekl/adminer/datetime";
import { useConfirm } from "@marekl/adminer/hooks";
import { formatCalendar, formatDate, pluralizeCzech } from "@marekl/adminer/format";
import { AppTopbar } from "../../../shell/AppTopbar";
import { useTeam } from "../../../lib/queries/teams";
import { hasNoSeasonRegistration } from "../../../lib/memberState";
import {
  useSeasons,
  useTeamMembers,
  type Membership,
  type MembershipRole,
  type Person,
  type PersonPayload,
} from "../../../lib/queries/memberships";
import {
  parseRowErrors,
  useDiscardSeasonRegistrationDraft,
  useSeasonRegistrationDraft,
  useSubmitSeasonRegistration,
  type SeasonRegistrationEntry,
} from "../../../lib/queries/seasonRegistration";
import { ApiError } from "../../../lib/api";
import { documentsMovedCode, useTeamSeasonDocuments } from "../../../lib/queries/seasonDocuments";
import { DocumentConsentList } from "../../../components/DocumentConsentList";
import { allTicked } from "../../../lib/documentConsent";
import { useSeason } from "../../../season";
import { useMe, hasAnyGlobalRole, agreesForTeam } from "../../../auth/queries";
import { canRegisterTeam } from "../../../lib/entryEligibility";
import { UnsavedChangesModal, useUnsavedChangesGuard } from "@marekl/adminer/shell";
import { sortRoster } from "../../../lib/rosterOrder";
import { birthNumberDigits } from "../../../lib/birthNumber";
import { DraftRoster, type DraftRosterHandle } from "./DraftRoster";
import type { DraftPerson, DraftRow } from "./draftTypes";
import { draftReducer, initialDraft, rosterSignature, type RosterMergeAction } from "./draftReducer";
import { errorsByRow, isBlankNewRow, isDraftDirty, isRowDirty } from "./draftValidation";
import { DraftBar, type BlockingItem, type DraftCounts } from "./DraftBar";
import { TransferRevize } from "./TransferRevize";
import { draftChangeCount, parseDraftPayload } from "./draftPersist";
import { SavedDraftPanel } from "./SavedDraftPanel";
import { useDraftAutosave, type SaveStatus } from "./useDraftAutosave";
import styles from "./SeasonRegistrationPage.module.css";
import { candidateMatches } from "./revizeGroups";
import { seasonOrdinal } from "../../../lib/seasonPhase";
import type { ID } from "../../../lib/id";

export function SeasonRegistrationPage() {
  const params = useParams();
  const teamId = params.id ?? "";
  const validId = teamId !== "" ? teamId : undefined;

  const { data: me } = useMe();
  const { activeSeason, isActiveSelected, setSelectedSeasonId } = useSeason();
  const { data: team, isLoading: teamLoading } = useTeam(validId);

  const isMembersAdmin = hasAnyGlobalRole(me, "admin", "members_admin");
  const registrationOpen = !!activeSeason?.registration_open;
  // Shared with every „Registrace do sezóny" button pointing here, so a
  // button is never offered to somebody this page would bounce.
  const canManageTeam = canRegisterTeam(me, teamId, team?.federation_id);

  // This workspace only ever writes into the active season.
  useEffect(() => {
    if (!isActiveSelected) setSelectedSeasonId(null);
  }, [isActiveSelected, setSelectedSeasonId]);

  if (!me || teamLoading) return <LoadingState label="Načítám…" />;
  if (!validId || !canManageTeam) {
    return <Navigate to={validId ? `/teams/${validId}` : "/dashboard"} replace />;
  }
  // The route is linkable and the rail offers it, so the page refuses a
  // club outside ČNSMS itself rather than opening a grid whose submit the
  // server would reject (membership.ErrSeasonRegistrationNotForTeam).
  // „Registrace do sezóny" is ČNSMS's soupiska; a club under another
  // federation never files one.
  if (team && team.federation_is_primary === false) {
    return (
      <>
        <AppTopbar crumbs={[{ label: team.name, href: `/teams/${teamId}` }, { label: "Registrace" }]} />
        <Page>
          <Page.Content>
            <EmptyState
              title="Registrace do sezóny se vašeho týmu netýká"
              description="Registrace do sezóny se týká týmů ČNSMS. Váš tým patří pod jinou federaci; na pohárové soutěže v Česku se přihlašuje přímo, bez registrace."
              action={
                <Link to={`/teams/${teamId}/souteze`}>
                  <Button variant="primary">Soutěže a přihlášky</Button>
                </Link>
              }
            />
          </Page.Content>
        </Page>
      </>
    );
  }
  if (!activeSeason) {
    return (
      <>
        <AppTopbar crumbs={[{ label: "Registrace" }]} />
        <Page>
          <Page.Content>
            <EmptyState
              title="Žádná aktivní sezóna"
              description="Registraci lze provést jen v aktivní sezóně."
              action={
                isMembersAdmin ? (
                  <Link to="/admin/sezony">
                    <Button variant="primary">Spravovat sezóny</Button>
                  </Link>
                ) : undefined
              }
            />
          </Page.Content>
        </Page>
      </>
    );
  }
  if (!registrationOpen && !isMembersAdmin) {
    return <Navigate to={`/teams/${teamId}`} replace />;
  }
  // Keyed by team: everything the workspace remembers (the draft, whether a
  // stored one was answered, the revision autosave builds on) is one team's,
  // and must not carry over to another team's registration route.
  return <RegistrationWorkspace key={teamId} teamId={teamId} teamName={team?.name} />;
}

/**
 * The draft bar's word on the rozpracovaná registrace: only when something is
 * wrong. Saving is routine and happens every few seconds, so „Ukládám…" /
 * „Uloženo 15:00" would only flicker in the bar (decision of 2026-11-01).
 */
function saveStatusText(st: SaveStatus): string | undefined {
  switch (st.kind) {
    case "failed":
      return "Rozpracovanou registraci se nepodařilo uložit";
    case "conflict":
      return "Ukládání zastaveno";
    default:
      return undefined;
  }
}

/** The writable fields of a Person, as the submit's person_base sends them. */
function personPayload(p: Person): PersonPayload {
  return {
    first_name: p.first_name,
    last_name: p.last_name,
    country_code: p.country_code,
    birth_date: p.birth_date,
    birth_number: p.birth_number,
    street: p.street,
    city: p.city,
    postal_code: p.postal_code,
    phone: p.phone,
  };
}

function RegistrationWorkspace({ teamId, teamName }: { teamId: ID; teamName?: string }) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { activeSeason } = useSeason();
  const { data: me } = useMe();
  const { data: team } = useTeam(teamId);
  const { data: rawMembers = [], isLoading } = useTeamMembers(teamId);
  const submit = useSubmitSeasonRegistration(teamId);
  const leaderPersonId = team?.leader?.person.id ?? null;
  // The draft opens in the same order the roster page shows: leader,
  // trenéři a asistenti, then members, Czech collation by surname. Sorted
  // once here so the hydrate signature and the rows agree; the reducer
  // itself never reorders, which is what keeps a freshly typed row from
  // jumping (the grid's own header sort is a display-order layer on top).
  const members = useMemo(
    () => sortRoster(rawMembers, leaderPersonId ?? undefined),
    [rawMembers, leaderPersonId],
  );
  const isMembersAdmin = hasAnyGlobalRole(me, "admin", "members_admin");
  const seasonCode = activeSeason?.code ?? "";
  const deadline = activeSeason?.registration_open_to;

  const [draft, dispatch] = useReducer(draftReducer, initialDraft);
  // Set by a successful submit, consumed by the effect that leaves the page.
  const [done, setDone] = useState<{ created: number; updated: number; removed: number } | null>(
    null,
  );

  const dirty = isDraftDirty(draft);

  // The rozpracovaná registrace (docs/rozpracovana-registrace.md). Only the
  // team's own leader writes it, and only while the window is open; anybody
  // else who may read the team sees it read-only (SavedDraftPanel).
  const draftQuery = useSeasonRegistrationDraft(teamId);
  const discardSaved = useDiscardSeasonRegistrationDraft(teamId);
  const savedDraft = draftQuery.data?.draft ?? null;
  const savedPayload = useMemo(
    () => (savedDraft ? parseDraftPayload(savedDraft.payload) : null),
    [savedDraft],
  );
  // Only an answer fetched on THIS visit counts (the query refetches on
  // mount): a cached one could be minutes old and offer a stale revision.
  const draftReady = draftQuery.isFetchedAfterMount && !draftQuery.isError;
  const draftFailed = draftQuery.isFetchedAfterMount && draftQuery.isError;
  const canWriteDraft = draftReady && (draftQuery.data?.can_write ?? false);
  // A stored draft waits for „Pokračovat" / „Začít znovu". Until that is
  // answered nothing autosaves, or the empty workspace would overwrite it.
  // Decided once per visit: a later refetch of the draft must not ask again.
  const [resumeDecided, setResumeDecided] = useState(false);
  const resumePending = canWriteDraft && savedPayload !== null && !resumeDecided;
  const autosave = useDraftAutosave({
    teamId,
    draft,
    enabled: canWriteDraft && resumeDecided && draft.hydrated,
  });
  const beginAutosave = autosave.begin;
  useEffect(() => {
    if (resumeDecided || !draftReady || !canWriteDraft || savedPayload) return;
    // Nothing to offer. A stored draft this page cannot read (an older
    // format) is built over rather than offered.
    if (savedDraft) beginAutosave(savedDraft.revision);
    setResumeDecided(true);
  }, [resumeDecided, draftReady, canWriteDraft, savedPayload, savedDraft, beginAutosave]);

  // Whether leaving keeps the work: autosave is on and its last word was not
  // a failure or a paused conflict.
  const keepsWork =
    canWriteDraft && autosave.status.kind !== "conflict" && autosave.status.kind !== "failed";
  // Asked whenever the registration has unsent changes, saved or not: the
  // rozpracovaná registrace keeps the work, but leaving the page by accident
  // still costs the leader the place they were at. The dialog says what is
  // kept (UnsavedChangesModal below).
  const guard = useUnsavedChangesGuard(dirty);

  // The workspace has done its job; what the leader wants next is the team,
  // with the roster as it now stands. The counts travel along so the team
  // page can confirm what was written. Waiting for the draft to go clean is
  // the point: navigating while it is still dirty trips the guard, which
  // blocks the redirect and then asks about work that is already saved.
  useEffect(() => {
    if (!done || dirty) return;
    navigate(`/teams/${teamId}`, {
      replace: true,
      state: { registration: { season: seasonCode, ...done } },
    });
  }, [done, dirty, navigate, teamId, seasonCode]);

  // Hydrate from the server roster. A refetch that disagrees with what we
  // hydrated from is surfaced rather than applied — overwriting would throw
  // away whatever the leader has typed. `hydrated` is the first-run flag: an
  // empty roster's signature is "", indistinguishable from the initial
  // state, so comparing signatures alone left a brand-new team with no
  // trailing blank row — and no way to register anyone.
  const signature = rosterSignature(members);
  const stale = draft.hydrated && draft.rosterSignature !== signature && dirty;
  useEffect(() => {
    if (isLoading) return;
    if (draft.hydrated && draft.rosterSignature === signature) return;
    if (dirty) return; // handled by the stale banner below
    dispatch({ type: "HYDRATE", members, leaderPersonId });
    // members is derived from signature; depending on it directly would
    // re-run this on every refetch that changed nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, isLoading, dirty, leaderPersonId, draft.hydrated]);

  // The team query can resolve after the members query, so the first
  // HYDRATE may have run with no leader id — and the signature guard above
  // would never let it be applied. SET_LEADER re-marks the rows without
  // touching anyone's typing.
  useEffect(() => {
    if (!draft.hydrated) return;
    if (draft.leaderPersonId === leaderPersonId) return;
    dispatch({ type: "SET_LEADER", leaderPersonId });
  }, [draft.hydrated, draft.leaderPersonId, leaderPersonId]);

  // Whether the club is already in this season, and therefore whether this
  // press is JOINING one or editing a soupiska that is already in it. It
  // decides what the primary says, whether an empty draft may be submitted,
  // which rows block it, and which rows are sent.
  const clubRegistered = !hasNoSeasonRegistration(team?.state_issues ?? []);
  const joining = !clubRegistered;
  // Joining makes the leader a registered ČNSMS member too, and the server
  // refuses that while their rodné číslo is empty
  // (membership.ErrLeaderBirthNumberMissing). Only while joining: once the
  // club is in the season the row exists and this is not the gate any more.
  // Digits, not trim: the server asks birthnumber.Digits, so a legacy value
  // holding no digits at all („-", „neuvedeno") is empty to it and must be
  // empty here too, or the refusal arrives with no warning before it.
  //
  // Cleared as soon as the draft supplies one. A members_admin may edit the
  // leader's identity cells, and the submit accepts a number typed there, so
  // the warning has to follow the draft rather than only the stored person.
  const leaderRowHasNumber = draft.rows.some(
    (r) => r.isLeader && !r.removed && birthNumberDigits(r.person.birth_number ?? "") !== "",
  );
  const leaderNeedsBirthNumber =
    joining &&
    !birthNumberDigits(team?.leader?.person.birth_number ?? "") &&
    !leaderRowHasNumber;

  // Season documents still waiting for this club's consent: none, or every
  // one when it joins, or a document added or replaced since it did. The
  // server asks every pending one on any submit (seasondoc.CheckTx).
  const documents = useTeamSeasonDocuments(teamId);
  // Only the leader agrees for the club (rbac.AgreesForTeam). Anybody else
  // registering it skips the ticks; the server records nothing for them
  // and the documents stay pending for the leader.
  const agrees = agreesForTeam(me, teamId);
  const pendingDocs = useMemo(
    () => (documents.data ?? []).filter((d) => !d.agreed),
    [documents.data],
  );
  // What this user is asked to tick: everything pending for the leader,
  // nothing for anybody else.
  const docsToAgree = useMemo(() => (agrees ? pendingDocs : []), [agrees, pendingDocs]);
  const [ticked, setTicked] = useState<ReadonlySet<ID>>(() => new Set());
  const toggleDocument = useCallback((versionId: ID, checked: boolean) => {
    setTicked((prev) => {
      const next = new Set(prev);
      if (checked) next.add(versionId);
      else next.delete(versionId);
      return next;
    });
  }, []);
  // The confirmation the primary opens: always when joining (it carries the
  // fee), and on an ordinary submit only while a document is pending, since
  // the ticks live in it.
  const [confirming, setConfirming] = useState<"join" | "documents" | null>(null);
  // A documents refusal stays in the dialog, where the ticks are.
  const [confirmError, setConfirmError] = useState<unknown>(null);
  const openConfirm = (kind: "join" | "documents") => {
    setTicked(new Set());
    setConfirmError(null);
    setConfirming(kind);
  };

  // Scrolled to when a submit fails; see onSubmit.
  const alertsRef = useRef<HTMLDivElement>(null);
  const errors = useMemo(() => errorsByRow(draft, joining), [draft, joining]);
  // The grid always carries a trailing blank row; it is the "next line", not a
  // member, so neither the count nor the empty state may see it.
  const peopleCount = draft.rows.filter((r) => !isBlankNewRow(r)).length;
  // Not canSubmit(draft): that helper re-runs the full validation pass this
  // component just memoized into `errors`, doubling per-keystroke work.
  // A club joining the season may press with nothing staged; one that is
  // already in it needs something to send.
  // …and not while the leader has no rodné číslo: the server refuses that
  // submit (ErrLeaderBirthNumberMissing), so offering the press would let a
  // leader type in a whole soupiska, lose it to the refusal, and lose it again
  // on the next reload — the draft lives in this page and nowhere else. The
  // Alert above says why, and for a Správce členské základny it says where;
  // typing the number into the leader's row clears this and the press returns.
  // A registered club with a document pending (added or replaced since it
  // agreed) may press with an empty draft too: agreeing is then the change.
  const ready =
    (joining || dirty || docsToAgree.length > 0) &&
    errors.size === 0 &&
    !leaderNeedsBirthNumber &&
    !documents.isLoading &&
    !documents.isError &&
    !submit.isPending;

  // --- transfer candidates -------------------------------------------------
  // The source-season roster is loaded here, not inside TransferRevize,
  // because two consumers share it: the revize panel and the blank row's
  // autocomplete. A candidate is someone from the source season who is not
  // on the roster, not the leader, and not already staged in the draft.
  const { data: seasons = [], isLoading: seasonsLoading } = useSeasons();
  // Only seasons BEHIND the one being registered into, newest first.
  //
  // „not active" is not the same test: a season the federation has
  // already opened for next year is inactive too, and it can hold no
  // roster to transfer — writes only ever target the active season — so
  // offering it means offering a source that is empty by construction.
  // It also sorts to the top of a descending list, which is how the
  // auto-pick below came to open an empty panel on a season that has not
  // happened yet.
  //
  // NOT the strict `<` that decides whether a season is closed, though.
  // seasonOrdinal reads the LAST year in the code, so „2025/2026" and
  // „2026" both answer 2026 — and if the federation ever names two
  // consecutive seasons that way, a strict test would drop the only
  // season anyone could transfer from and leave the panel with nothing to
  // offer. A tie is kept; the worst it can cost is one empty roster,
  // against a panel that silently does not work.
  const selectableSeasons = useMemo(() => {
    const target = activeSeason ? seasonOrdinal(activeSeason.code) : null;
    return seasons
      .filter((s) => {
        if (s.active) return false;
        // Neither end placeable: offer it rather than hide it. Guessing
        // is what the null is there to prevent.
        if (target === null) return true;
        const o = seasonOrdinal(s.code);
        return o === null || o <= target;
      })
      .sort((a, b) => (seasonOrdinal(b.code) ?? -Infinity) - (seasonOrdinal(a.code) ?? -Infinity));
  }, [seasons, activeSeason]);
  const sourceMembers = useTeamMembers(teamId, draft.sourceSeasonId ?? undefined);
  // Somebody registered with another club this season has left: the server
  // refuses to transfer them, so they are listed apart rather than offered
  // (pre-selected, in an opt-out panel).
  const { candidates, departed } = useMemo(() => {
    // Anybody the draft already holds, by person rather than by key: a row
    // the merge left orphaned („m:…") names a person who is a candidate again
    // once removed, and offering them would stage them twice.
    const staged = new Set(draft.rows.flatMap((r) => (r.origin.kind === "new" ? [] : [r.origin.personId])));
    const roster = new Set(members.map((m) => m.person.id));
    const open = (sourceMembers.data ?? []).filter(
      (m) => !roster.has(m.person.id) && m.person.id !== leaderPersonId && !staged.has(m.person.id),
    );
    return {
      candidates: open.filter((m) => !m.registered_elsewhere),
      departed: open.filter((m) => !!m.registered_elsewhere),
    };
  }, [sourceMembers.data, members, leaderPersonId, draft.rows]);

  // Nobody on this season's soupiska yet. The leader counts: a leader-only
  // soupiska is a cup-only club with a nečlen leader, whose last season was
  // pohárovka entrants, and bulk-transferring those would register them.
  const rosterEmpty = members.length === 0;

  // Two steps on one page. An empty soupiska with somebody to bring over
  // opens on the transfer alone: for a returning club that is most of the
  // work, and a thirty-column sheet under it made the one decision on screen
  // hard to find. Committing or skipping it collapses the panel and shows the
  // sheet. A soupiska that already has people, or a club with nobody in the
  // auto-picked season (a new one), opens straight on the sheet. Decided
  // once, when the roster and the candidates are in; after that only the
  // buttons move it.
  const [step, setStep] = useState<"transfer" | "roster" | null>(null);
  useEffect(() => {
    if (step !== null) return;
    if (isLoading || seasonsLoading || !draft.hydrated) return;
    if (!rosterEmpty || selectableSeasons.length === 0) {
      setStep("roster");
      return;
    }
    // Wait for the auto-pick below and for that season's roster.
    if (draft.sourceSeasonId == null || sourceMembers.isLoading) return;
    setStep(candidates.length > 0 ? "transfer" : "roster");
  }, [
    step,
    isLoading,
    seasonsLoading,
    draft.hydrated,
    rosterEmpty,
    selectableSeasons.length,
    draft.sourceSeasonId,
    sourceMembers.isLoading,
    candidates.length,
  ]);
  const toRoster = useCallback(() => {
    setStep("roster");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  // Once the sheet has been on screen, leaving the transfer is going back to
  // it, not skipping a step.
  const [sheetSeen, setSheetSeen] = useState(false);
  useEffect(() => {
    if (step === "roster") setSheetSeen(true);
  }, [step]);
  const stagedTransfers = draft.rows.filter((r) => r.origin.kind === "transfer").length;

  // An EMPTY soupiska opens on the most recent past season.
  //
  // The panel otherwise opens on „Vyberte sezónu" deliberately: a team that
  // has already registered gets a quiet card instead of a wall of chips,
  // and the transfer stays an explicit act. That reasoning does not hold
  // when there is nothing to be quiet about — a leader looking at an empty
  // roster is there to fill it, and the season select is the only way in.
  //
  // Once only, and only while the draft is untouched: this must never
  // reassert itself over a leader who deliberately cleared the select.
  const autoPicked = useRef(false);
  useEffect(() => {
    if (autoPicked.current) return;
    if (isLoading || !draft.hydrated) return;
    if (!rosterEmpty || draft.sourceSeasonId != null || dirty) return;
    // Already newest-first, and already past-only.
    const latest = selectableSeasons[0];
    if (!latest) return;
    autoPicked.current = true;
    dispatch({ type: "SET_SOURCE_SEASON", seasonId: latest.id });
  }, [isLoading, draft.hydrated, draft.sourceSeasonId, rosterEmpty, dirty, selectableSeasons]);

  // Stable identities: GridRow/CardRow are memoized, and an inline closure
  // here would hand every row a fresh prop each render, re-rendering the
  // whole roster per keystroke anyway. dispatch is stable by contract.
  const onCell = useCallback(
    (key: string, field: keyof DraftPerson, value: DraftPerson[keyof DraftPerson]) =>
      dispatch({ type: "SET_CELL", key, field, value }),
    [],
  );
  const onRoles = useCallback(
    (key: string, roles: MembershipRole[]) => dispatch({ type: "SET_ROLES", key, roles }),
    [],
  );
  const onRemove = useCallback(
    (row: DraftRow) =>
      dispatch(
        row.origin.kind === "persisted"
          ? { type: "TOGGLE_REMOVE", key: row.key }
          : { type: "DROP_STAGED", key: row.key },
      ),
    [],
  );
  // One TOGGLE_TRANSFER per person: candidates exclude the already-staged,
  // so every toggle is an add, and existing staged rows survive — which is
  // what lets the residue panel stage one more later without eating the
  // first batch (SET_ALL_TRANSFERS would).
  // Committing is also the end of the transfer step: the people just staged
  // are rows on the sheet now, and the sheet is where they get checked.
  const onCommitTransfers = useCallback(
    (selected: Membership[]) => {
      for (const m of selected) {
        dispatch({ type: "TOGGLE_TRANSFER", membership: m, leaderPersonId });
      }
      toRoster();
    },
    [leaderPersonId, toRoster],
  );
  // The blank row's autocomplete picked a person: stage their transfer and
  // forget the half-typed row that served as the search box.
  const onPickCandidate = useCallback(
    (row: DraftRow, m: Membership) => {
      dispatch({ type: "TOGGLE_TRANSFER", membership: m, leaderPersonId });
      dispatch({ type: "DROP_STAGED", key: row.key });
    },
    [leaderPersonId],
  );
  const suggestCandidates = useCallback(
    (q: string) => candidates.filter((m) => candidateMatches(m, q)).slice(0, 8),
    [candidates],
  );
  // RESET first: plain HYDRATE deliberately keeps staged transfer/new rows,
  // which is right for the background refresh and exactly wrong for a
  // control that promises to discard them.
  // RESET + HYDRATE, not a plain HYDRATE: hydration deliberately preserves
  // staged rows, which is exactly what this button promises to throw away.
  // „Pokračovat": the saved rows, merged with the roster as it is now, so
  // whatever changed meanwhile is taken in and what both sides changed is
  // asked (draftRebase). The sheet, not the transfer: the work is there.
  const resumeDraft = useCallback(() => {
    if (!savedDraft || !savedPayload) return;
    dispatch({
      type: "RESTORE",
      payload: savedPayload,
      members,
      leaderPersonId,
      canEditIdentity: isMembersAdmin,
    });
    beginAutosave(savedDraft.revision);
    setResumeDecided(true);
    setStep("roster");
  }, [savedDraft, savedPayload, members, leaderPersonId, isMembersAdmin, beginAutosave]);

  const startFresh = useCallback(
    () =>
      confirm({
        title: "Začít znovu",
        description: "Rozpracovaná registrace se zahodí a začnete od začátku. Tuto akci nelze vrátit zpět.",
        confirmLabel: "Zahodit a začít znovu",
        variant: "danger",
        onConfirm: async () => {
          await discardSaved.mutateAsync();
          beginAutosave(0);
          setResumeDecided(true);
        },
      }),
    [confirm, discardSaved, beginAutosave],
  );

  // Another tab, or another device, saved first. „Načíst uloženou verzi"
  // takes theirs, merged like any return to a saved draft.
  const adoptSaved = useCallback(async () => {
    const res = await draftQuery.refetch();
    const d = res.data?.draft;
    const p = d ? parseDraftPayload(d.payload) : null;
    if (!d || !p) return;
    dispatch({ type: "RESTORE", payload: p, members, leaderPersonId, canEditIdentity: isMembersAdmin });
    beginAutosave(d.revision);
  }, [draftQuery, members, leaderPersonId, isMembersAdmin, beginAutosave]);

  const discardDraft = useCallback(() => {
    dispatch({ type: "RESET" });
    dispatch({ type: "HYDRATE", members, leaderPersonId });
  }, [members, leaderPersonId]);

  // Keeps the work and takes the server's changes in around it
  // (draftRebase); whatever both sides changed is left for the leader to
  // decide, row by row.
  const mergeDraft = useCallback(() => {
    dispatch({
      type: "REBASE",
      members,
      leaderPersonId,
      candidates: sourceMembers.data ?? [],
      canEditIdentity: isMembersAdmin,
    });
  }, [members, leaderPersonId, sourceMembers.data, isMembersAdmin]);

  const activeSeasonId = activeSeason?.id;
  const onMerge = useCallback(
    (a: RosterMergeAction) => {
      switch (a.type) {
        case "REREGISTER":
          if (activeSeasonId) dispatch({ ...a, seasonId: activeSeasonId });
          return;
        case "MERGE_DUPLICATE":
          dispatch({ ...a, canEditIdentity: isMembersAdmin });
          return;
        default:
          dispatch(a);
      }
    },
    [activeSeasonId, isMembersAdmin],
  );

  // Asked, not just done. The draft is the only place this work exists —
  // nothing is written until the submit — so a misplaced click here costs
  // the whole session's typing.
  const askDiscard = useCallback(
    () =>
      confirm({
        title: "Zahodit změny",
        description:
          "Rozpracované změny se zahodí a soupiska se vrátí do aktuálního stavu. Tuto akci nelze vrátit zpět.",
        confirmLabel: "Zahodit změny",
        variant: "danger",
        onConfirm: discardDraft,
      }),
    [confirm, discardDraft],
  );

  // Switching the source season drops the staged transfers, and it has to:
  // the submit carries ONE `from_season_id` for the whole batch and a
  // transfer row sends only a person_id, so a row staged from last season
  // would be written as if it came from the newly picked one. That makes
  // the wipe correct — but it was silent, and a leader who had already
  // corrected thirty transferred rows lost all of it to one select.
  //
  // So it is only asked when there is something to lose.
  const setSourceSeason = useCallback(
    (seasonId: ID | null) => {
      const staged = stagedTransfers;
      if (staged === 0) {
        dispatch({ type: "SET_SOURCE_SEASON", seasonId });
        return;
      }
      confirm({
        // Warning: staged transfers are dropped, but nothing is written
        // and nothing on the server changes.
        title: "Změnit zdrojovou sezónu",
        description: `Změnou sezóny se ze seznamu odeberou převedení členové (${staged}). Členové, které jste zapsali ručně, zůstanou zachováni. Pokud chcete, můžete nejprve odeslat registraci a pak ji doplnit členy z jiné sezóny.`,
        confirmLabel: "Změnit sezónu",
        variant: "danger",
        onConfirm: () => dispatch({ type: "SET_SOURCE_SEASON", seasonId }),
      });
    },
    [confirm, stagedTransfers],
  );
  // The roster opens the person, scrolls to them and flashes the line, so
  // the eye lands on the right one even when the scroll was short.
  const rosterRef = useRef<DraftRosterHandle>(null);
  const jumpToRow = useCallback((key: string) => rosterRef.current?.reveal(key), []);

  // The bar's chip opens these as a list: who blocks the submit and what
  // they are missing. errorsByRow iterates draft.rows, so the list keeps
  // the on-screen order.
  const blockingItems = useMemo<BlockingItem[]>(() => {
    const byKey = new Map(draft.rows.map((r) => [r.key, r]));
    const items: BlockingItem[] = [];
    for (const [key, errs] of errors) {
      const row = byKey.get(key);
      const name = row
        ? `${row.person.last_name} ${row.person.first_name}`.trim()
        : "";
      // A conflict, a removed or a doubled person is something to decide,
      // not data to fill in; the chip must not call it „Chybí údaje".
      const decide = Boolean(
        row && (row.orphaned || row.duplicateOf || Object.keys(row.conflicts).length > 0),
      );
      items.push({ key, label: name || "Nový řádek", message: Object.values(errs)[0] ?? "", decide });
    }
    return items;
  }, [errors, draft.rows]);

  const draftCounts = useMemo<DraftCounts>(() => {
    const c = { transfers: 0, added: 0, edited: 0, removed: 0 };
    for (const r of draft.rows) {
      if (!isRowDirty(r)) continue;
      if (r.origin.kind === "transfer") c.transfers++;
      else if (r.origin.kind === "new") c.added++;
      else if (r.removed) c.removed++;
      else c.edited++;
    }
    return c;
  }, [draft.rows]);

  // Joining the season is the act that makes the club a ČNSMS member for it,
  // with a fee attached, so it asks first. Editing a soupiska that is already
  // registered does not, unless a document is waiting for consent.
  function requestSubmit() {
    if (joining) openConfirm("join");
    else if (docsToAgree.length > 0) openConfirm("documents");
    else void onSubmit();
  }
  const people = draft.rows.filter((r) => !isBlankNewRow(r)).length;

  // Resolves to the refusal, or null once the registration is written: the
  // confirmation dialog keeps a documents refusal, the page shows the rest.
  async function onSubmit(): Promise<unknown> {
    const entries: SeasonRegistrationEntry[] = [];
    for (const row of draft.rows) {
      if (row.origin.kind === "persisted" && row.removed) {
        entries.push({ row_key: row.key, membership_id: row.origin.membershipId, remove: true });
        continue;
      }
      // Only rows the leader actually touched are sent. An untouched member
      // stays off the payload entirely, so a legacy incomplete record cannot
      // block a submit that has nothing to do with it — the server would
      // otherwise validate it as a registration and reject the whole batch.
      //
      // A club JOINING the season is the exception: registering carries the
      // whole soupiska across, so every row goes, touched or not. (The server
      // sweeps up anything left out anyway — membership.unnamedGuestRows —
      // but sending them keeps the per-row errors bound to rows the grid can
      // scroll to.)
      if (isBlankNewRow(row)) continue;
      // …and the leader's untouched row is left off the payload for the same
      // reason: naming it would make the server validate it as an ordinary
      // entry (validatePersonFull) and undo the exemption it grants itself.
      if (joining && row.isLeader && !isRowDirty(row)) continue;
      if (!joining && !isRowDirty(row)) continue;
      const entry: SeasonRegistrationEntry = {
        row_key: row.key,
        roles: row.roles,
        person: {
          first_name: row.person.first_name,
          last_name: row.person.last_name,
          country_code: row.person.country_code,
          birth_date: toApiDate(row.person.birth_date) ?? "",
          birth_number: row.person.birth_number,
          street: row.person.street,
          city: row.person.city,
          postal_code: row.person.postal_code,
          phone: row.person.phone,
        },
      };
      // What the row was edited FROM, so the server writes only what the
      // leader changed and keeps whatever somebody else changed meanwhile.
      if (row.baseline) entry.person_base = personPayload(row.baseline);
      if (row.origin.kind === "persisted") {
        entry.membership_id = row.origin.membershipId;
        entry.roles_base = row.baselineRoles;
      }
      if (row.origin.kind === "transfer") entry.person_id = row.origin.personId;
      entries.push(entry);
    }
    // A save in flight lands first, then none starts until the submit is
    // done: the submit deletes the draft at the revision it names, and a
    // save landing meanwhile would outlive it.
    const draftRevision = await autosave.holdForSubmit();
    try {
      const res = await submit.mutateAsync({
        from_season_id: draft.sourceSeasonId ?? undefined,
        entries,
        agreed_document_version_ids: docsToAgree
          .filter((d) => ticked.has(d.version_id))
          .map((d) => d.version_id),
        draft_revision: draftRevision,
      });
      autosave.releaseAfterSubmit(true);
      // The draft is done: mutateAsync only resolves once the roster refetch
      // has landed, so dropping it lets the effect below hydrate the členy
      // as the server now has it — every submitted row included, exactly once.
      // RESET also clears the dirty flag, which is what lets the navigation
      // below through the unsaved-changes guard.
      dispatch({ type: "RESET" });
      // Leaving is queued, not done here: `dispatch` has not been applied
      // yet, so the draft still reads dirty this tick and the guard would
      // block the very navigation the submit earned. The effect above goes
      // once the draft is clean.
      setDone({ created: res.created, updated: res.updated, removed: res.removed });
      return null;
    } catch (err) {
      autosave.releaseAfterSubmit(false);
      // The documents moved under the page. „changed": a ticked version was
      // replaced, so the ticks go. „not agreed": usually a document added
      // since the page loaded, which the card does not list yet. Either way
      // the card has to show the current list, or every retry fails the
      // same way over a checkbox that is not there.
      const code = documentsMovedCode(err);
      if (code === "documents_changed") setTicked(new Set());
      if (code) void documents.refetch();
      const keys = new Set(draft.rows.map((r) => r.key));
      // A field changed on both sides comes back with the server's value and
      // lands on its row as a conflict to decide, no merge round trip needed.
      const { byRow, message, conflicts } = parseRowErrors(err, keys);
      // A 401 arrives as the server's own English "session expired". The
      // modal from SessionExpiredModal explains it, but the page must not
      // be left claiming something was saved.
      const expired = err instanceof ApiError && err.status === 401;
      // A proxy 502, a network drop or any non-JSON body yields neither row
      // errors nor a message — and the mutation suppresses the global toast,
      // so without a fallback the spinner just stopped and the leader had no
      // idea the registration was never submitted.
      const fallback = expired
        ? "Přihlášení vypršelo, registrace se neodeslala. Přihlaste se znovu a registraci odešlete ještě jednou."
        : byRow.size === 0 && !message
          ? "Registraci se nepodařilo odeslat. Zkontrolujte připojení a zkuste to znovu."
          : message;
      dispatch({
        type: "APPLY_SERVER_ERRORS",
        byRow,
        message: fallback,
        conflicts,
        canEditIdentity: isMembersAdmin,
      });
      // The messages sit above a grid that can run to thirty rows, so on a
      // long roster a failed submit changed nothing the leader could see.
      alertsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return err;
    }
  }

  return (
    <>
      <AppTopbar
        crumbs={[
          { label: "Týmy", href: "/admin/tymy" },
          { label: teamName ?? "Tým", href: `/teams/${teamId}` },
          { label: "Registrace" },
        ]}
      />
      <Page>
        <Page.Header
          title={`Registrace do sezóny ${seasonCode}`}
          subtitle={teamName ? `Soupiska týmu ${teamName}` : undefined}
          actions={
            deadline ? (
              <Badge variant="info">Registrační okno do {formatDate(deadline)}</Badge>
            ) : undefined
          }
        />
        <Page.Content stack gap="lg">
          {stale && (
            <Alert variant="warn" title="Soupisku mezitím změnil někdo jiný">
              Vaše rozpracované změny zůstaly zachované.{" "}
              <Button variant="primary" onClick={mergeDraft}>
                Načíst změny a pokračovat
              </Button>{" "}
              <Button onClick={discardDraft}>
                Načíst znovu (zahodí rozpracované změny)
              </Button>
            </Alert>
          )}
          {draftFailed && (
            // Nothing is lost: the guard falls back to plain „neuložené
            // změny". But the leader must know nothing is being saved.
            <Alert variant="warn" title="Rozpracovanou registraci se nepodařilo načíst">
              Dokud se nenačte, změny se průběžně neukládají.{" "}
              <Button size="sm" onClick={() => void draftQuery.refetch()}>
                Načíst znovu
              </Button>
            </Alert>
          )}
          {autosave.status.kind === "conflict" && (
            <Alert
              variant="warn"
              // Only the team's leader writes the draft, so another save is
              // practically always theirs from another window; the server's
              // sentence names a person and agrees a verb with them.
              title={
                autosave.status.body.revision > 0
                  ? "Rozpracovanou registraci jste mezitím uložili v jiném okně nebo na jiném zařízení."
                  : "Rozpracovaná registrace mezitím zanikla: byla odeslána nebo zahozena."
              }
            >
              Automatické ukládání jsme zastavili, aby se vaše a uložená verze navzájem
              nepřepsaly.{" "}
              {autosave.status.body.revision > 0 && (
                <>
                  <Button variant="primary" onClick={() => void adoptSaved()}>
                    Načíst uloženou verzi
                  </Button>{" "}
                </>
              )}
              <Button onClick={autosave.overwrite}>
                {autosave.status.body.revision > 0 ? "Přepsat ji mou verzí" : "Uložit mou verzi"}
              </Button>
            </Alert>
          )}
          {!canWriteDraft && savedDraft && savedPayload && (
            <SavedDraftPanel
              payload={savedPayload}
              updatedMs={savedDraft.updated_ms}
              updatedByName={savedDraft.updated_by_name}
            />
          )}
          {draft.rebaseNotices.length > 0 && (
            <Alert
              variant="info"
              title="Soupiska se mezitím změnila"
              action={
                <Button size="sm" onClick={() => dispatch({ type: "CLEAR_REBASE_NOTICES" })}>
                  Rozumím
                </Button>
              }
            >
              <ul>
                {draft.rebaseNotices.map((n, i) => (
                  <li key={i}>
                    {n.message}
                    {n.key && (
                      <>
                        {" "}
                        <Button weight="ghost" size="sm" onClick={() => n.key && jumpToRow(n.key)}>
                          Ukázat
                        </Button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </Alert>
          )}
          <div ref={alertsRef} />
          {draft.formMessage && <Alert variant="err">{draft.formMessage}</Alert>}
          {documents.isError && (
            // Without the list there is nothing to tick, and the server asks
            // for every pending document, so the press stays off until it loads.
            <Alert variant="err" title="Dokumenty sezóny se nepodařilo načíst">
              Registraci nelze odeslat bez souhlasu s dokumenty.{" "}
              <Button size="sm" onClick={() => void documents.refetch()}>
                Načíst znovu
              </Button>
            </Alert>
          )}

          {leaderNeedsBirthNumber && (
            // Said here rather than only on submit. The server refuses the
            // registration over this (ErrLeaderBirthNumberMissing) and the
            // leader cannot clear it themselves, so finding out after typing in
            // thirty lidí would be the worst moment to learn it.
            <Alert variant="warn" title="Vedoucí nemá vyplněné rodné číslo">
              Registrací se z vedoucího stává člen ČNSMS, a ten se bez rodného
              čísla zaevidovat nedá, takže tým zatím zaregistrovat nejde.{" "}
              {isMembersAdmin
                ? // Řekne, kde to spravit, protože tenhle čtenář na to právo má:
                  // zámek identity vyjímá právě Správce členské základny, takže
                  // buňku v řádku vedoucího otevřenou má a odeslání číslo z ní
                  // přijme.
                  step === "transfer"
                    ? "Doplňte ho v řádku vedoucího na soupisce, v dalším kroku."
                    : "Doplňte ho v řádku vedoucího v tabulce níže."
                : // Bez výzvy „soupisku si zatím připravte": rozpracovaná
                  // tabulka žije jen v téhle stránce, takže by to znamenalo
                  // vyplnit třicet řádků a přijít o ně při prvním načtení.
                  "Požádejte ČNSMS o doplnění; do té doby se registrace nedá dokončit."}
            </Alert>
          )}

          {!draftQuery.isFetchedAfterMount ? (
            // Whether a stored draft waits to be offered decides what may be
            // typed into, so the sheet waits for the answer.
            <LoadingState label="Načítám soupisku…" />
          ) : resumePending && savedDraft && savedPayload ? (
            <Card>
              <Card.Header
                title="Máte rozpracovanou registraci"
                subtitle={`Uloženo ${formatCalendar(dayjs(savedDraft.updated_ms))}${
                  savedDraft.updated_by_name ? ` · ${savedDraft.updated_by_name}` : ""
                } · změny u ${draftChangeCount(savedPayload)} ${pluralizeCzech(draftChangeCount(savedPayload), ["osoby", "osob", "osob"])}`}
              />
              <Card.Content>
                <p>
                  Můžete pokračovat tam, kde jste skončili. Co se mezitím na soupisce
                  změnilo, zůstane zachováno spolu s vašimi úpravami.
                </p>
              </Card.Content>
              <Card.Footer>
                {/* Card.Footer is only the closing region; it lays nothing
                    out, so the buttons need their own row (adminer-redesign §40). */}
                <div className={styles.actions}>
                  <Button variant="primary" onClick={resumeDraft} disabled={isLoading}>
                    Pokračovat
                  </Button>
                  <Button onClick={startFresh}>Začít znovu</Button>
                </div>
              </Card.Footer>
            </Card>
          ) : step === null ? (
            <LoadingState label="Načítám soupisku…" />
          ) : (
            <>
              {activeSeason && (
                <TransferRevize
                  candidates={candidates}
                  departed={departed}
                  candidatesLoading={sourceMembers.isLoading}
                  selectableSeasons={selectableSeasons}
                  sourceSeasonId={draft.sourceSeasonId}
                  targetSeason={activeSeason}
                  onSetSeason={setSourceSeason}
                  onCommit={onCommitTransfers}
                  open={step === "transfer"}
                  stagedCount={stagedTransfers}
                  onOpen={() => setStep("transfer")}
                  onLeave={toRoster}
                  // Skipping reads as skipping only the first time through;
                  // reopened from the sheet, the way out is back to it.
                  leaveLabel={sheetSeen ? "Zpět na soupisku" : "Přeskočit převod"}
                />
              )}

              {step === "roster" && (
                <>

                  {/* The roster IS the page, so it gets no Card (AGENTS §5): a
                      section header above, the framed list below. */}
                  <div>
                    <Page.SectionHeader
                      title={`Členové sezóny ${seasonCode}`}
                      meta={`${peopleCount} ${pluralizeCzech(peopleCount, ["osoba", "osoby", "osob"])}`}
                    />
                    <DraftRoster
                      ref={rosterRef}
                      rows={draft.rows}
                      errors={errors}
                      canEditIdentity={isMembersAdmin}
                      onCell={onCell}
                      onRoles={onRoles}
                      onRemove={onRemove}
                      onMerge={onMerge}
                      suggest={candidates.length > 0 ? suggestCandidates : undefined}
                      onPick={onPickCandidate}
                    />
                  </div>

                  <DraftBar
                    counts={draftCounts}
                    blockingItems={blockingItems}
                    dirty={dirty}
                    ready={ready}
                    submitting={submit.isPending}
                    clubRegistered={clubRegistered}
                    documentsUnready={documents.isLoading || documents.isError}
                    onSubmit={requestSubmit}
                    onDiscard={askDiscard}
                    onJumpTo={jumpToRow}
                    saveStatus={canWriteDraft ? saveStatusText(autosave.status) : undefined}
                  />
                </>
              )}
            </>
          )}
        </Page.Content>
      </Page>

      <ConfirmDialog
        open={confirming !== null}
        title={confirming === "join" ? "Registrovat tým do sezóny" : "Souhlas s dokumenty"}
        confirmLabel={confirming === "join" ? "Registrovat tým" : "Odeslat registraci"}
        busy={submit.isPending}
        confirmDisabled={!allTicked(docsToAgree, ticked)}
        onClose={() => setConfirming(null)}
        error={confirmError}
        onConfirm={async () => {
          const err = await onSubmit();
          if (documentsMovedCode(err)) {
            setConfirmError(err);
            return;
          }
          setConfirming(null);
        }}
      >
        {confirming === "join" && (
          <>
            <p>
              Chcete se zaregistrovat pro účast na postupových soutěžích ČNSMS?
              Podmínkou dokončení registrace je uhrazení členského příspěvku po
              ukončení registračního okna.
            </p>
            <p>
              Tým <strong>{team?.name}</strong> se stane členem ČNSMS pro sezónu{" "}
              <strong>{activeSeason?.code}</strong>, spolu s vedoucím a{" "}
              {people === 0 ? (
                "nikým dalším; členy doplníte později"
              ) : (
                <>
                  {people} {pluralizeCzech(people, ["osobou", "osobami", "osobami"])} na
                  soupisce
                </>
              )}
              .
            </p>
          </>
        )}
        {confirming === "documents" && (
          <p>Od poslední registrace přibyly dokumenty sezóny, které tým zatím neodsouhlasil.</p>
        )}
        {!agrees && pendingDocs.length > 0 && (
          <p>
            Souhlas s dokumenty sezóny potvrdí vedoucí týmu na stránce týmu. Do té doby u týmu
            zůstane „Chybí souhlas s dokumenty“.
          </p>
        )}
        {docsToAgree.length > 0 && (
          <DocumentConsentList
            documents={docsToAgree}
            ticked={ticked}
            onToggle={toggleDocument}
            missingMessage="Registraci lze odeslat až po zaškrtnutí všech dokumentů."
            disabled={submit.isPending}
          />
        )}
      </ConfirmDialog>

      <UnsavedChangesModal
        open={guard.blocked}
        // When autosave keeps the work, leaving discards nothing, so the
        // dialog must not say it does: the lib's defaults („Zahodit neuložené
        // změny?", „Zahodit změny") are for the cases where it really goes.
        // Not when the last save failed or is paused on a conflict: then
        // nothing guarantees the work is stored. The confirm stays red, the
        // preset's only variant (docs/adminer-redesign.md §41).
        {...(keepsWork
          ? {
              title: "Opustit registraci?",
              description:
                "Registrace do sezóny zatím nebyla odeslána. Vaše změny zůstanou uložené jako rozpracovaná registrace a můžete se k nim později vrátit.",
              confirmLabel: "Opustit stránku",
            }
          : {
              description:
                "Registrace do sezóny zatím nebyla odeslána. Opustit stránku a zahodit rozpracované změny?",
            })}
        onClose={guard.cancel}
        onConfirm={() => {
          void autosave.flushNow();
          guard.proceed();
        }}
      />
    </>
  );
}
